import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RapportoOreConsuntivo } from '../models/types';
import { rapportiOreService } from '../services/api';
import './OreConfermatePage.css';

// Icone SVG
const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12,6 12,12 16,14"></polyline>
  </svg>
);

const IconCalendar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const IconFileText = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14,2 14,8 20,8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10,9 9,9 8,9"></polyline>
  </svg>
);

const IconBriefcase = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const IconCheckCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const IconRefresh = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const IconChevronDown = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const IconChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const OreConfermatePage: React.FC = () => {
  const { user } = useAuth();
  const [rapporti, setRapporti] = useState<RapportoOreConsuntivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroOperaio, setFiltroOperaio] = useState<number | undefined>(undefined);
  const [totaleOre, setTotaleOre] = useState(0);
  const [dateEspanse, setDateEspanse] = useState<Set<string>>(new Set());

  // Ottieni gli operai associati all'utente
  const operai = user?.operai || (user?.operaio ? [user.operaio] : []);
  const operaioSingolo = operai.length === 1;

  // Funzione per toggle expand/collapse di una data
  const toggleData = (data: string) => {
    setDateEspanse(prev => {
      const nuovoSet = new Set(prev);
      if (nuovoSet.has(data)) {
        nuovoSet.delete(data);
      } else {
        nuovoSet.add(data);
      }
      return nuovoSet;
    });
  };

  // Se c'� un solo operaio, imposta automaticamente il filtro
  useEffect(() => {
    if (operaioSingolo && operai.length > 0) {
      setFiltroOperaio(operai[0].id);
    }
  }, [operaioSingolo, operai]);

  // Carica i rapporti
  const caricaRapporti = async () => {
    setLoading(true);
    setError(null);
    setDateEspanse(new Set()); // Reset delle date espanse

    try {
      // Se c'� un solo operaio, usa sempre il suo ID
      const operaioIdDaUsare = operaioSingolo && operai.length > 0 
        ? operai[0].id 
        : filtroOperaio;

      const response = await rapportiOreService.getUltimaSettimanaConfermati(operaioIdDaUsare);

      if (response.success && response.data) {
        setRapporti(response.data);

        // Calcola totale ore
        const totale = response.data.reduce((sum, r) => sum + r.ore, 0);
        setTotaleOre(totale);
      } else {
        setError(response.message || 'Errore nel caricamento dei rapporti');
      }
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei rapporti');
    } finally {
      setLoading(false);
    }
  };

  // Carica i rapporti al mount e quando cambia il filtro
  useEffect(() => {
    caricaRapporti();
  }, [filtroOperaio]);

  // Formattazione data
  const formatData = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('it-IT', { 
      weekday: 'short',
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Formattazione ore
  const formatOre = (ore: number) => {
    return ore.toFixed(1);
  };

  // Raggruppa i rapporti per data
  const rapportiPerData = rapporti.reduce((acc, rapporto) => {
    const data = rapporto.data.split('T')[0];
    if (!acc[data]) {
      acc[data] = [];
    }
    acc[data].push(rapporto);
    return acc;
  }, {} as Record<string, RapportoOreConsuntivo[]>);

  // Ordina le date in modo decrescente
  const dateOrdinate = Object.keys(rapportiPerData).sort((a, b) => b.localeCompare(a));

  return (
    <div className="ore-confermate-page">
      <div className="page-header">
        <div className="header-left">
          <IconCheckCircle />
          <div>
            <h1>Ore Confermate  -  Ultimi sette giorni</h1>
            <p className="subtitle">Ore confermate in consuntivo negli ultimi 7 giorni</p>
          </div>
        </div>
        <button 
          className="btn-refresh" 
          onClick={caricaRapporti}
          disabled={loading}
          title="Aggiorna"
        >
          <IconRefresh />
        </button>
      </div>

      {/* Filtri */}
      {user?.operai && user.operai.length > 1 && (
        <div className="filters">
          <label>
            <IconUser />
            <span>Operaio:</span>
            <select 
              value={filtroOperaio || ''} 
              onChange={(e) => setFiltroOperaio(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Tutti</option>
              {user.operai.map(op => (
                <option key={op.id} value={op.id}>{op.nome}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Riepilogo */}
      <div className="summary-card">
        <div className="summary-item">
          <span className="summary-label">Rapporti trovati</span>
          <span className="summary-value">{rapporti.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Totale ore</span>
          <span className="summary-value highlight">{formatOre(totaleOre)} h</span>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Caricamento in corso...</p>
        </div>
      )}

      {/* Errore */}
      {error && (
        <div className="error-message">
          <p>?? {error}</p>
        </div>
      )}

      {/* Lista rapporti */}
      {!loading && !error && rapporti.length === 0 && (
        <div className="empty-state">
          <IconCheckCircle />
          <p>Nessun rapporto confermato nell'ultima settimana</p>
        </div>
      )}

      {!loading && !error && rapporti.length > 0 && (
        <div className="rapporti-list">
          {dateOrdinate.map(data => {
            const rapportiGiorno = rapportiPerData[data];
            const oreGiorno = rapportiGiorno.reduce((sum, r) => sum + r.ore, 0);
            const isEspansa = dateEspanse.has(data);

            return (
              <div key={data} className="data-group">
                <div 
                  className="data-header clickable" 
                  onClick={() => toggleData(data)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleData(data);
                    }
                  }}
                >
                  <div className="data-info">
                    <span className="chevron-icon">
                      {isEspansa ? <IconChevronDown /> : <IconChevronRight />}
                    </span>
                    <IconCalendar />
                    <span className="data-text">{formatData(data)}</span>
                    <span className="rapporti-count">({rapportiGiorno.length})</span>
                  </div>
                  <span className="data-ore">{formatOre(oreGiorno)} h</span>
                </div>

                {isEspansa && (
                  <div className="rapporti-cards">
                    {rapportiGiorno.map(rapporto => (
                      <div key={rapporto.id} className="rapporto-card">
                        <div className="card-header">
                          <div className="ore-badge">
                            <IconClock />
                            <span>{formatOre(rapporto.ore)} h</span>
                          </div>
                          <div className="consuntivo-badge">
                            <IconFileText />
                            <span>Cons. {rapporto.consuntivoNumero}</span>
                          </div>
                        </div>

                        <div className="card-body">
                          <div className="info-row">
                            <IconUser />
                            <span className="label">Operaio:</span>
                            <span className="value">{rapporto.operaioNome}</span>
                          </div>

                          <div className="info-row">
                            <IconBriefcase />
                            <span className="label">Commessa:</span>
                            <span className="value">{rapporto.commessaTitolo}</span>
                          </div>

                          <div className="info-row">
                            <IconBriefcase />
                            <span className="label">Sottocommessa:</span>
                            <span className="value">{rapporto.sottocommessaNome}</span>
                          </div>

                          {rapporto.descrizione && (
                            <div className="descrizione">
                              <p>{rapporto.descrizione}</p>
                            </div>
                          )}

                          {rapporto.consuntivoData && (
                            <div className="consuntivo-info">
                              <IconCalendar />
                              <span>Data consuntivo: {formatData(rapporto.consuntivoData)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OreConfermatePage;
